import * as Sentry from "@sentry/nuxt"
import { outboundModifiers } from "~/utils/outboundModifiers"
import {
	EVENTS,
	track,
	orderProps,
	incrementPeople,
	setPeopleOnce,
	moneyGbp,
	gbpDelta,
	numeric,
	enumValue,
	dateOnly,
	entityId,
} from "~/utils/analytics"

// Map known backend errors (or generic 500s with a known root cause) into
// admin-friendly copy. Returns null when the error shape is unrecognised so
// callers fall back to the generic message.
// PUT /orders/:id returns 409 `{ staleOrder: true, message }` when the body's
// updatedAt is older than the row's. Other 409s (active PedalMe booking) are not this.
export const isStaleOrderConflict = (error: any): boolean => {
	const status = error?.response?.status ?? error?.statusCode
	const body = error?.response?._data ?? error?.data
	return status === 409 && body?.staleOrder === true
}

export const describeUpdateProductsError = (error: any): string | null => {
	const status = error?.response?.status ?? error?.statusCode
	const body = error?.response?._data ?? error?.data
	const message = typeof body === "string" ? body : body?.message || error?.message || ""

	if (message.includes("Modifier quantity must match product amount")) {
		return "One or more products have modifier selections that don't match the product quantity. Click the edit icon on the affected item to rebalance the modifiers (e.g. proteins must total the platter quantity)."
	}
	if (status === 500) {
		// updateOrderProducts currently returns 500 for any CartError thrown from
		// processItemsOrder (incl. split_quantity mismatch). Until the backend
		// surfaces the specific message, give admins something more actionable
		// than a blank wall.
		return "Couldn't save these product changes. The most common cause is a modifier mismatch — when a product has 'one selection per unit' modifiers (e.g. proteins), the modifier quantities must total the product quantity. Try editing the affected item, or contact support if you're stuck."
	}
	return null
}

const CANCELLATION_REASON_ANALYTICS_VALUES = new Set([
	"client_postponed",
	"dietary",
	"below_minimum",
	"maker_availability",
	"other",
])

const analyticsCancellationReason = (value: unknown): string | undefined => {
	const reason = enumValue(value)
	if (reason === undefined) return undefined
	return CANCELLATION_REASON_ANALYTICS_VALUES.has(reason) ? reason : "other"
}

// Only the latest getOrderLedgers call may write rows, report errors or clear the loading
// flag — a superseded request settling late must not touch any of them.
let latestLedgersRequest = 0

const productStoreModule = {
	namespaced: true,
	state: () => ({
		ordersLoading: false,
		orders: false,
		calculatedOrder: null,
		newOrder: false,
		createOrderLoading: false,
		chosenOrder: false,
		chosenOrderLedgers: [],
		chosenOrderLedgersLoading: false,
		orderLoading: false,
		updateProductsLoading: false,
		updateProductsError: false,
		regenerateInvoiceLoading: false,
		regenerateInvoiceError: false,
	}),
	mutations: {
		SET_ORDERS_LOADING(state, payload) {
			state.ordersLoading = payload
		},
		SET_ORDERS(state, payload) {
			state.orders = payload
		},
		SET_CALCULATED_ORDER(state, payload) {
			state.calculatedOrder = payload
		},
		SET_NEW_ORDER(state, payload) {
			state.newOrder = payload
		},
		SET_CREATE_ORDER_LOADING(state, payload) {
			state.createOrderLoading = payload
		},
		SET_CHOSEN_ORDER(state, payload) {
			state.chosenOrder = payload
		},
		// Replaces ONLY the nested delivery row, leaving every other field of chosenOrder
		// untouched. The status poller (utils/deliveryStatusPoller.ts) uses this instead of
		// re-running getOrderById, because `chosenOrder` is also the edit buffer that
		// `updateOrder` serialises wholesale as the PUT body — a background full-order
		// replace would silently discard whatever the admin was part-way through editing.
		//
		// Whole-object replace, never a field-by-field merge: a refresh can move status,
		// tracking_link, last_synced_at, last_synced_by and provider_state_at together, and a
		// partial merge drifts the moment a field is added server-side. The manual Refresh lands
		// the PUT's re-read row through the same mutation (plugins/vuex.ts commitDeliveryBooking).
		SET_CHOSEN_ORDER_DELIVERY(state, payload) {
			if (!state.chosenOrder) return
			state.chosenOrder = { ...state.chosenOrder, delivery: payload }
		},
		// Narrow: a booking cancel bumps the row's updatedAt mid-edit; refresh only that
		// so the next PUT isn't stale, without discarding the buffer's unsaved edits.
		SET_CHOSEN_ORDER_UPDATED_AT(state, payload) {
			if (state.chosenOrder) state.chosenOrder.updatedAt = payload
		},
		SET_CHOSEN_ORDER_LEDGERS(state, payload) {
			state.chosenOrderLedgers = payload
		},
		SET_CHOSEN_ORDER_LEDGERS_LOADING(state, payload) {
			state.chosenOrderLedgersLoading = payload
		},
		SET_ORDER_LOADING(state, payload) {
			state.orderLoading = payload
		},
		SET_UPDATE_PRODUCTS_LOADING(state, payload) {
			state.updateProductsLoading = payload
		},
		SET_UPDATE_PRODUCTS_ERROR(state, payload) {
			state.updateProductsError = payload
		},
		SET_REGENERATE_INVOICE_LOADING(state, payload) {
			state.regenerateInvoiceLoading = payload
		},
		SET_REGENERATE_INVOICE_ERROR(state, payload) {
			state.regenerateInvoiceError = payload
		},
	},
	actions: {
		async getOrderById({ commit, dispatch }, id) {
			commit("SET_ORDER_LOADING", true)
			const config = {
				headers: {
					accept: "application/json",
					"x-access-token-user": useCookie("auth_token").value ?? "",
					"content-type": "application/json",
				},
			}

			await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/users/orders/${id}`, config)
				.then(async (response) => {
					// Order line items are returned bare — bulk-fetch product images
					// and merge them onto each line item (mirrors getPackages).
					const orderProducts = response.orderProducts ?? []
					const productIds = orderProducts
						.map((op: any) => op.product?.id)
						.filter((pid: any) => pid !== undefined && pid !== null)
					if (productIds.length) {
						const byId = await dispatch(
							"fetchBulkAttachments",
							{ modelName: "product", ids: productIds, collections: ["mainImage"] },
							{ root: true }
						)
						response.orderProducts = orderProducts.map((op: any) =>
							op.product
								? {
										...op,
										product: {
											...op.product,
											attachments: {
												...(op.product.attachments || {}),
												mainImage: byId[op.product.id]?.mainImage ?? [],
											},
										},
									}
								: op
						)
					}
					commit("SET_CHOSEN_ORDER", response)
					// Always reflect the loaded order's delivery — clearing it when the
					// order has no booking. A conditional commit would leave a previous
					// order's booking in state, wrongly surfacing "View Booking" here.
					commit("SET_DELIVERY_BOOKING", response.delivery ?? false, { root: true })
					commit("SET_ORDER_LOADING", false)
				})
				.catch((error) => {
					commit("SET_ORDER_LOADING", false)
					dispatch("handleError", { error }, { root: true })
				})
		},
		// getOrder no longer returns ledger rows to the hub — the transaction history page
		// reads them from this admin-only endpoint.
		async getOrderLedgers({ commit, dispatch, state }, id) {
			commit("SET_CHOSEN_ORDER_LEDGERS", [])
			commit("SET_CHOSEN_ORDER_LEDGERS_LOADING", true)
			const request = ++latestLedgersRequest
			const config = {
				headers: {
					accept: "application/json",
					"x-access-token-user": useCookie("auth_token").value ?? "",
					"content-type": "application/json",
				},
			}

			await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/users/orders/${id}/ledgers`, config)
				.then((response) => {
					// A slower response for the same order must not overwrite the newer one.
					if (request !== latestLedgersRequest) return
					if (String(state.chosenOrder?.id) === String(id)) {
						commit("SET_CHOSEN_ORDER_LEDGERS", response)
					}
				})
				.catch((error) => {
					if (request !== latestLedgersRequest) return
					dispatch("handleError", { error }, { root: true })
				})
				.finally(() => {
					if (request === latestLedgersRequest) {
						commit("SET_CHOSEN_ORDER_LEDGERS_LOADING", false)
					}
				})
		},
		// Supersedes any in-flight getOrderLedgers, so a late response cannot re-commit rows
		// after logout. The loading flag is reset here because that request's finally no
		// longer owns it once the counter has moved.
		resetLedgers({ commit }) {
			latestLedgersRequest++
			commit("SET_CHOSEN_ORDER_LEDGERS", [])
			commit("SET_CHOSEN_ORDER_LEDGERS_LOADING", false)
		},
		getOrders: debounce(async function ({ commit, dispatch }, payload) {
			const filters = payload?.filters || {}
			const page = payload?.page || 1
			const limit = payload?.limit || 20
			const orderBy = payload?.orderBy || "createdAt"
			const orderDirection = payload?.orderDirection || "DESC"

			!payload && commit("SET_ORDERS_LOADING", true)

			const queryParams = new URLSearchParams({
				page: page.toString(),
				limit: limit.toString(),
				orderBy,
				orderDirection,
			}).toString()

			const config = {
				method: "POST",
				body: JSON.stringify({
					filters,
				}),

				headers: {
					accept: "application/json",
					"x-access-token-user": useCookie("auth_token").value ?? "",
					"content-type": "application/json",
				},
			}

			await $fetch(
				`${import.meta.env.VITE_AND_DINE_API}/api/users/orders/filter?${queryParams}`,
				config
			)
				.then((response) => {
					commit("SET_ORDERS", response.data)
					commit("SET_PAGES", response.paginate, { root: true })
					commit("SET_ORDERS_LOADING", false)
				})
				.catch((error) => {
					commit("SET_ORDERS_LOADING", false)
					dispatch("handleError", { error }, { root: true })
				})
		}, 200),
		async getAnalyticsOrders({ commit, dispatch }, payload) {
			commit("SET_ORDERS_LOADING", true)

			const params = new URLSearchParams()
			if (payload?.maker_id) params.set("maker_id", payload.maker_id)
			if (payload?.from) params.set("from", payload.from)
			if (payload?.to) params.set("to", payload.to)

			const config = {
				headers: {
					accept: "application/json",
					"x-access-token-user": useCookie("auth_token").value ?? "",
				},
			}

			await $fetch(
				`${import.meta.env.VITE_AND_DINE_API}/api/users/analytics/orders?${params.toString()}`,
				config
			)
				.then((response) => {
					commit("SET_ORDERS", response.orders)
					commit("SET_ORDERS_LOADING", false)
				})
				.catch((error) => {
					commit("SET_ORDERS_LOADING", false)
					dispatch("handleError", { error }, { root: true })
				})
		},
		calculateHubOrder: debounce(async function ({ commit, state }, payload) {
			if (!payload?.maker_id) {
				commit("SET_ORDERS_LOADING", false)
				commit("SET_CALCULATED_ORDER", {
					error: "Missing maker or items/packages",
				})
				return
			}

			const isDaily = payload.type === "Daily"
			const isSet = payload.package_type_id === 1
			const usePackages = isSet && isDaily
			if (usePackages && payload.packages?.length === 0) {
				commit("SET_ORDERS_LOADING", false)
				commit("SET_CALCULATED_ORDER", {
					error: "Missing maker or items/packages",
				})
				return
			} else if (!usePackages && payload.items?.length === 0) {
				commit("SET_ORDERS_LOADING", false)
				commit("SET_CALCULATED_ORDER", {
					error: "Missing maker or items/packages",
				})
				return
			}

			const normalizedItems = (payload.items || [])
				.map(({ product, quantity, appliedModifiers = [] }) => ({
					product_id: product?.id || null,
					quantity,
					// The modifier quantity MUST be forwarded. Dropping it left the backend to fall
					// back to 1, so this preview under-priced every multi-unit line relative to what
					// createOrder (which posts newOrder verbatim, quantity included) actually
					// charged — and it broke split_quantity groups outright, whose shares have to
					// sum to the line amount or the backend rejects the cart.
					//
					// The value is the LINE TOTAL (picked x line quantity), scaled where the admin
					// picks — see updateModifiers on pages/orders/new.vue. The backend multiplies
					// nothing; it charges price * quantity.
					//
					// Deselected rows (quantity 0) are DROPPED, not forwarded — the backend's
					// `|| 1` would promote a 0 to one charged portion, so a preview that forwards
					// them prices an add-on the admin removed. See utils/outboundModifiers.ts.
					appliedModifiers: outboundModifiers(appliedModifiers),
				}))
				.filter((i) => !!i.product_id)

			const body = {
				customer_id: payload.customer_id || null,
				items: usePackages ? [] : normalizedItems,
				packages: usePackages ? payload.packages || [] : [],
				custom_delivery: payload.custom_delivery || false,
				delivery_total: payload.delivery_net || 0,
				orderFees: (payload.orderFees || [])
					.filter((f) => f.price_net > 0)
					.map(({ type, price_net, description }) => ({
						type,
						price_net: Number(price_net) || 0,
						description: description || "",
					})),
				service_fee_rate: payload.service_fee_rate || "0",
				discount_code: payload.discount_code || null,
				type: payload.type,
			}

			commit("SET_ORDERS_LOADING", true)

			await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/users/orders/calculateCartAdmin`, {
				method: "POST",
				body,
				headers: {
					accept: "application/json",
					"content-type": "application/json",
					"x-access-token-user": useCookie("auth_token").value || "",
				},
			})
				.then((response) => {
					commit("SET_CALCULATED_ORDER", response)
					commit("SET_ERROR_MESSAGE", false, { root: true })
				})
				.catch((e) => {
					const err = e.response?._data?.message || "Unknown error"
					commit("SET_CALCULATED_ORDER", { error: err })
					commit("SET_ERROR_MESSAGE", err, { root: true })
				})
				.finally(() => {
					commit("SET_ORDERS_LOADING", false)
				})
		}, 200),
		// Reports are now generated asynchronously via the file-generation queue.
		// This enqueues the report; the backend emails the requester a download
		// link when it's ready (and it appears in the generation history).
		async downloadCSV({ dispatch, commit }, payload) {
			const config = {
				headers: {
					accept: "application/json",
					"x-access-token-user": useCookie("auth_token").value ?? "",
				},
			}

			await $fetch(
				`${import.meta.env.VITE_AND_DINE_API}/api/users/orders/${
					payload.request
				}?type=${payload.type}&from=${payload.from}&to=${payload.to}`,
				config
			)
				.then(() => {
					const from = payload.from ? new Date(payload.from) : null
					const to = payload.to ? new Date(payload.to) : null
					track(EVENTS.REPORT_QUEUED, {
						report_type: enumValue(payload.type ?? payload.request),
						report_request: enumValue(payload.request),
						from_date: dateOnly(payload.from),
						to_date: dateOnly(payload.to),
						// Range width tells us which reports to cache or make synchronous.
						period_days:
							from && to && !Number.isNaN(from.getTime()) && !Number.isNaN(to.getTime())
								? Math.round((to.getTime() - from.getTime()) / 86400000)
								: undefined,
					})
					incrementPeople({ reports_queued_count: 1 })
					commit(
						"SET_SUCCESS_MESSAGE",
						"Report queued — we'll email you a download link when it's ready.",
						{ root: true }
					)
					setTimeout(() => commit("SET_SUCCESS_MESSAGE", false, { root: true }), 5000)
				})
				.catch((error) => {
					dispatch("handleError", { error }, { root: true })
				})
		},
		async createOrder({ state, commit, dispatch }) {
			// Sanitise the modifier rows on the way out. This posts newOrder essentially verbatim, so
			// it is the last point before a charge — and items can reach newOrder from paths that never
			// went through the picker (reorder, share-cart), carrying legacy `quantity: 0` rows that
			// the backend's `|| 1` would bill as one portion each. Everything else about newOrder is
			// deliberately passed through unchanged.
			const newOrder = state.newOrder
				? {
						...state.newOrder,
						...(Array.isArray(state.newOrder.items)
							? {
									items: state.newOrder.items.map((item) => ({
										...item,
										appliedModifiers: outboundModifiers(item.appliedModifiers),
									})),
								}
							: {}),
					}
				: state.newOrder
			const body = JSON.stringify(newOrder)
			const config = {
				headers: {
					accept: "application/json",
					"x-access-token-user": useCookie("auth_token").value ?? "",
					"content-type": "application/json",
				},
				method: "POST",
				body,
			}

			commit("SET_CREATE_ORDER_LOADING", true)

			await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/users/orders`, config)
				.then((response) => {
					// ★ The Value Moment. Fired inside .then() — every action in this
					// store swallows its error without rethrowing, so anything tracked
					// after `await createOrder()` in a component would fire on failure
					// too. duration_ms comes from the timer armed in pages/orders/new.vue.
					// newOrder is the OUTBOUND form payload and carries no money at all —
					// `total` and `items_gross` are computed server-side. Reading them off
					// it left the Value Moment with no revenue and skipped the lifetime
					// counter on every order. createOrderAdmin answers 201 with the
					// created Order row, which is authoritative; state.calculatedOrder
					// (what calculateHubOrder priced) is the fallback for a thin body.
					const created: any = response && typeof response === "object" ? (response as any) : {}
					const priced: any = (state as any).calculatedOrder || {}
					const createdOrder = {
						...newOrder,
						...created,
						total: created.total ?? priced.total,
						items_gross: created.items_gross ?? priced.items_gross,
					}
					const totalGbp = moneyGbp(createdOrder.total)
					track(EVENTS.ORDER_CREATED, {
						...orderProps(createdOrder),
						created_order_id: entityId(created.id),
						generate_invoice: Boolean(newOrder?.generate_invoice),
						package_type_id: entityId(newOrder?.package_type_id),
						delivery_net_gbp: moneyGbp(newOrder?.delivery_net),
						// The form field is service_fee_rate (getOrderPayload builds the
						// payload from it); there is no service_fee on newOrder.
						service_fee_rate: numeric(newOrder?.service_fee_rate ?? created.service_fee_rate),
						cost_centre_id: entityId(newOrder?.cost_centre_id),
						sales_contact_user_id: newOrder?.user_id,
					})
					incrementPeople({
						orders_created_count: 1,
						// increment, NOT people.track_charge — hub users are staff
						// creating orders somebody else pays for, so $transactions
						// would corrupt every out-of-the-box revenue report.
						//
						// Explicitly `> 0`, not truthy: moneyGbp permits negatives
						// (a fully-credited order), and a truthy check would let one
						// DECREMENT a lifetime counter. Lifetime value is monotonic by
						// definition — a negative or zero total simply contributes
						// nothing rather than eroding the total.
						...(totalGbp !== undefined && totalGbp > 0
							? { lifetime_order_value_gbp: totalGbp }
							: {}),
					})
					setPeopleOnce({ first_order_created_at: new Date().toISOString() })

					commit("SET_SUCCESS_MESSAGE", "Successfully created order", { root: true })
					commit("SET_NEW_ORDER", false)
					commit("SET_CREATE_ORDER_LOADING", false)
					setTimeout(() => {
						commit("SET_SUCCESS_MESSAGE", false, { root: true })
						navigateTo(`/orders/${response.id}`)
					}, 2000)
				})
				.catch((error) => {
					commit("SET_CREATE_ORDER_LOADING", false)
					dispatch("handleError", { error, description: "create order" }, { root: true })
				})
		},
		// `silent` skips the "Successfully updated order" banner — used for background
		// saves (e.g. persisting the hypercare flag from inside the Book Delivery
		// flow) that shouldn't flash a page-level success toast mid-interaction. The
		// error path still surfaces, and the refetch still runs, in both modes.
		async updateOrder({ state, commit, dispatch }, { silent = false } = {}) {
			const body = JSON.stringify(state.chosenOrder)
			const config = {
				headers: {
					accept: "application/json",
					"x-access-token-user": useCookie("auth_token").value ?? "",
					"content-type": "application/json",
				},
				method: "PUT",
				body,
			}

			// Returns true on success, false on failure. Callers that orchestrate around the
			// save (the delivery re-book flow, the sticky update bar) MUST check this: a
			// swallowed error that resolved silently made a failed PUT look saved — so a
			// cancel-then-fail re-book re-quoted from stale data and re-baselined the form. The
			// error toast still fires here; the boolean lets the caller abort.
			const orderId = state.chosenOrder.id
			return await $fetch(
				`${import.meta.env.VITE_AND_DINE_API}/api/users/orders/${orderId}`,
				config
			)
				.then(async () => {
					// Tracked regardless of `silent`: a silent save is still a save, so
					// it belongs in the data with is_silent as a property. Only the
					// user-facing banner is suppressed.
					track(EVENTS.ORDER_UPDATED, {
						...orderProps(state.chosenOrder),
						is_silent: Boolean(silent),
					})
					if (!silent) {
						commit("SET_SUCCESS_MESSAGE", "Successfully updated order", { root: true })
						setTimeout(() => {
							commit("SET_SUCCESS_MESSAGE", false, { root: true })
						}, 5000)
					}
					// Awaited: the re-read refreshes chosenOrder.updatedAt, which the next
					// PUT must carry or the backend rejects it as stale.
					await dispatch("getOrderById", orderId)
					return true
				})
				.catch(async (error) => {
					dispatch("handleError", { error, description: "updated order" }, { root: true })
					// Stale buffer: re-read so the next attempt carries the current
					// updatedAt. The backend's message tells the operator to re-apply.
					if (isStaleOrderConflict(error)) await dispatch("getOrderById", orderId)
					return false
				})
		},
		// Hub cancellation is button-only and goes through the admin-gated
		// `DELETE /api/users/orders/:id/cancel` endpoint (orderModificationController.cancelOrderAdmin).
		// The backend flips status synchronously and fires refunds; a partial/hard
		// refund failure still leaves the order Cancelled, with the existing
		// reconciliation alert email handling reconciliation offline. We refetch
		// once and let the UI reflect the new state — no polling.
		async cancelOrderAdmin({ state, commit, dispatch }, { orderId, cancellationReason }) {
			const body = JSON.stringify({ cancellation_reason: cancellationReason ?? null })
			const config = {
				headers: {
					accept: "application/json",
					"x-access-token-user": useCookie("auth_token").value ?? "",
					"content-type": "application/json",
				},
				method: "DELETE",
				body,
			}

			try {
				const response = await $fetch(
					`${import.meta.env.VITE_AND_DINE_API}/api/users/orders/${orderId}/cancel`,
					config
				)
				const partial = response?.status === "partial_failure"
				track(EVENTS.ORDER_CANCELLED, {
					// chosenOrder is the order being viewed; fall back to the bare id
					// when cancelling from the list page, where it isn't loaded.
					...orderProps(state?.chosenOrder?.id === orderId ? state.chosenOrder : { id: orderId }),
					cancellation_reason: analyticsCancellationReason(cancellationReason),
					is_legacy_path: false,
					refund_result: partial ? "partial_failure" : "full",
				})
				incrementPeople({ orders_cancelled_count: 1 })
				commit(
					"SET_SUCCESS_MESSAGE",
					partial
						? "Order cancelled — refund partially failed, manual reconciliation needed"
						: "Order cancelled",
					{ root: true }
				)
				setTimeout(() => {
					commit("SET_SUCCESS_MESSAGE", false, { root: true })
				}, 5000)
				await dispatch("getOrderById", orderId)
				return response
			} catch (error) {
				dispatch("handleError", { error, description: "cancel order" }, { root: true })
				throw error
			}
		},
		// ⚠️ STOPGAP — TODO(order-mods): REMOVE once the BE legacy guard ships.
		// Pre-ledger paid orders (paid + transaction_id, no SUCCEEDED PAY ledger) make
		// cancelOrderAdmin 500 — the Phase 2 refund path (executeChunkedRefund) throws
		// with no ledger to refund against. Until the backend routes legacy cancels
		// through the synchronous-cancel + reconciliation-alert path, we flip the status
		// here via changeStatus. This does NOT refund the customer and does NOT send the
		// reconciliation alert — the admin must refund manually in Stripe (the cancel
		// modal warns them). See docs/decisions.md "Hub legacy-order cancel stopgap".
		async cancelLegacyOrderAdmin({ state, commit, dispatch }, { orderId, cancellationReason }) {
			const headers = {
				accept: "application/json",
				"x-access-token-user": useCookie("auth_token").value ?? "",
				"content-type": "application/json",
			}
			const setField = (field, setValue) =>
				$fetch(`${import.meta.env.VITE_AND_DINE_API}/api/users/orders/changeStatus/${orderId}`, {
					method: "PUT",
					headers,
					body: JSON.stringify({ field, setValue }),
				})
			try {
				// status first — the critical flip. Reason/cancelled_at follow.
				await setField("status", "Cancelled")
				await setField("cancellation_reason", cancellationReason ?? null)
				await setField("cancelled_at", new Date().toISOString())
				// refund_result: manual_required — the stopgap path does NOT refund and
				// does NOT send the reconciliation alert. Counting these quantifies the
				// ops burden of the pre-ledger stopgap, i.e. the case for removing it.
				track(EVENTS.ORDER_CANCELLED, {
					...orderProps(state?.chosenOrder?.id === orderId ? state.chosenOrder : { id: orderId }),
					cancellation_reason: analyticsCancellationReason(cancellationReason),
					is_legacy_path: true,
					refund_result: "manual_required",
				})
				incrementPeople({ orders_cancelled_count: 1 })
				commit(
					"SET_SUCCESS_MESSAGE",
					"Order cancelled — this is a pre-ledger order, so you MUST refund the customer manually in Stripe.",
					{ root: true }
				)
				setTimeout(() => {
					commit("SET_SUCCESS_MESSAGE", false, { root: true })
				}, 8000)
				await dispatch("getOrderById", orderId)
			} catch (error) {
				dispatch("handleError", { error, description: "cancel legacy order" }, { root: true })
				throw error
			}
		},
		async validateOrder({ state, commit, dispatch }, order) {
			const body = JSON.stringify(order)
			const config = {
				headers: {
					accept: "application/json",
					"x-access-token-user": useCookie("auth_token").value ?? "",
					"content-type": "application/json",
				},
				method: "PUT",
				body,
			}
			commit("SET_LOADING", "Validating order changes...", { root: true })
			const returnVal = await $fetch(
				`${import.meta.env.VITE_AND_DINE_API}/api/users/orders/validateDiff/${order.id}`,
				config
			)
			commit("REMOVE_LOADING", null, { root: true })
			return returnVal
		},
		downloadInvoice({ dispatch, commit }, payload) {
			const config = {
				headers: {
					accept: "application/pdf",
					"x-access-token-user": useCookie("auth_token").value ?? "",
					"content-type": "application/octet-stream; charset=utf-8",
				},
				// Backend streams the PDF base64-encoded (Base64Encode) with no text
				// content-type, so ofetch would default to a Blob and atob() would
				// throw. Force text so `res` is the raw base64 string.
				responseType: "text" as const,
			}

			$fetch(
				`${import.meta.env.VITE_AND_DINE_API}/api/users/orders/downloadInvoice/${payload.orderId}`,
				config
			)
				.then((res) => {
					function base64ToArrayBuffer(data) {
						const binaryString = window.atob(data)
						const binaryLen = binaryString.length
						const bytes = new Uint8Array(binaryLen)
						for (let i = 0; i < binaryLen; i++) {
							const ascii = binaryString.charCodeAt(i)
							bytes[i] = ascii
						}
						return bytes
					}
					const arrayBuffer = base64ToArrayBuffer(res)
					const url = window.URL.createObjectURL(
						new Blob([arrayBuffer], {
							type: "application/pdf",
						})
					)
					const link = document.createElement("a")
					link.href = url
					link.setAttribute("download", `&Dine Invoice - ${payload.orderRef}.pdf`)
					document.body.appendChild(link)
					link.click()
					link.remove()

					// One event name, document_type discriminator. Deliberately NOT
					// timed — six call sites would arm and consume each other's clocks.
					track(EVENTS.DOCUMENT_DOWNLOADED, {
						document_type: "order_invoice",
						download_result: "success",
						order_id: entityId(payload.orderId),
						order_reference: payload.orderRef,
						source: enumValue(payload.source),
					})
				})
				.catch((error) => {
					Sentry.captureException(error)
					dispatch("handleError", { error, description: "download invoice" }, { root: true })
				})
		},
		// NOTE: downloadInvoiceIfExists was removed 2026-07-15 (review) — the
		// confirm-then-generate flow it backed is gone; the orders pages open the
		// FileHistoryModal instead (every revision listed, admin picks one).
		async generateAndDownloadInvoice({ dispatch, commit }, payload) {
			const { orderId } = payload
			// Drive the order-module loading flag ourselves — the root
			// `regenerateInvoice` action commits to a same-name root mutation
			// that doesn't exist, so its loading state never reaches the
			// detail page's button.
			commit("SET_REGENERATE_INVOICE_LOADING", true)
			try {
				// Regeneration is queue-only now (backend returns 202): the cron
				// generates the revision and emails the download link. No immediate
				// download any more.
				await dispatch("regenerateInvoice", { orderId }, { root: true })
				commit(
					"SET_SUCCESS_MESSAGE",
					"Invoice regeneration queued — you'll get an email with the download link when it's ready.",
					{ root: true }
				)
				setTimeout(() => commit("SET_SUCCESS_MESSAGE", false, { root: true }), 6000)
				// Regeneration rate is an invoice-correctness proxy: a high rate means
				// an upstream data problem, not an admin preference.
				track(EVENTS.INVOICE_REGENERATION_QUEUED, {
					order_id: entityId(orderId),
					order_reference: payload?.orderRef,
				})
				return { queued: true }
			} catch (error: any) {
				Sentry.captureException(error)
				dispatch("handleError", { error, description: "generate invoice" }, { root: true })
				throw error
			} finally {
				commit("SET_REGENERATE_INVOICE_LOADING", false)
			}
		},
		downloadChecklist({ dispatch }, payload) {
			// Backend sends the checklist as a raw PDF blob (Content-Type
			// application/pdf), unlike downloadInvoice which base64-streams a disk
			// file. So take it as a blob directly — no atob. Mirrors
			// downloadAllergenMatrix.
			$fetch(
				`${import.meta.env.VITE_AND_DINE_API}/api/users/orders/downloadChecklist/${payload.orderId}`,
				{
					headers: { "x-access-token-user": useCookie("auth_token").value ?? "" },
					responseType: "blob",
				}
			)
				.then((blob) => {
					const url = window.URL.createObjectURL(blob as Blob)
					const link = document.createElement("a")
					link.href = url
					link.setAttribute("download", `&Dine Checklist - ${payload.orderRef}.pdf`)
					document.body.appendChild(link)
					link.click()
					link.remove()
					window.URL.revokeObjectURL(url)
					track(EVENTS.DOCUMENT_DOWNLOADED, {
						document_type: "checklist",
						download_result: "success",
						order_id: entityId(payload.orderId),
						order_reference: payload.orderRef,
						source: enumValue(payload.source),
					})
				})
				.catch((error) => {
					Sentry.captureException(error)
					dispatch("handleError", { error, description: "download checklist" }, { root: true })
				})
		},
		downloadAllergenMatrix({ dispatch }, payload) {
			$fetch(
				`${import.meta.env.VITE_AND_DINE_API}/api/users/orders/downloadAllergenMatrix/${payload.orderId}`,
				{
					headers: { "x-access-token-user": useCookie("auth_token").value ?? "" },
					responseType: "blob",
				}
			)
				.then((blob) => {
					const url = window.URL.createObjectURL(blob as Blob)
					const link = document.createElement("a")
					link.href = url
					link.setAttribute("download", `&Dine Allergen Matrix - ${payload.orderRef}.pdf`)
					document.body.appendChild(link)
					link.click()
					link.remove()
					window.URL.revokeObjectURL(url)
					track(EVENTS.DOCUMENT_DOWNLOADED, {
						document_type: "allergen_matrix",
						download_result: "success",
						order_id: entityId(payload.orderId),
						order_reference: payload.orderRef,
						source: enumValue(payload.source),
					})
				})
				.catch((error) => {
					Sentry.captureException(error)
					dispatch(
						"handleError",
						{ error, description: "download allergen matrix" },
						{ root: true }
					)
				})
		},
		// Variant that resolves with { downloaded, missing } instead of swallowing
		// the result. Lets per-row table actions show a friendly "no maker
		// invoice yet" toast without triggering the generic handleError flow.
		async downloadMakerInvoiceIfExists({ dispatch }, payload) {
			const config = {
				headers: {
					accept: "application/json",
					"x-access-token-user": useCookie("auth_token").value ?? "",
				},
				method: "GET",
				responseType: "json",
			}
			try {
				const response: any = await $fetch(
					`${import.meta.env.VITE_AND_DINE_API}/api/users/makers/downloadMakerInvoice/${payload.orderId}`,
					config
				)
				const { files } = response || {}
				if (!files || !Array.isArray(files) || files.length === 0) {
					// download_result "missing" is a real broken-workflow counter: the
					// admin wanted a maker invoice and there wasn't one.
					track(EVENTS.DOCUMENT_DOWNLOADED, {
						document_type: "maker_invoice",
						download_result: "missing",
						order_id: entityId(payload.orderId),
						order_reference: payload.orderRef,
						source: enumValue(payload.source),
					})
					return { downloaded: false, missing: true }
				}
				files.forEach((file: { fileName: string; data: string; contentType: string }) => {
					if (!file.fileName || !file.data) return
					const byteCharacters = atob(file.data)
					const byteNumbers = new Array(byteCharacters.length)
					for (let i = 0; i < byteCharacters.length; i++) {
						byteNumbers[i] = byteCharacters.charCodeAt(i)
					}
					const byteArray = new Uint8Array(byteNumbers)
					const blob = new Blob([byteArray], { type: file.contentType || "application/pdf" })
					const url = window.URL.createObjectURL(blob)
					const link = document.createElement("a")
					link.href = url
					link.setAttribute("download", file.fileName)
					document.body.appendChild(link)
					link.click()
					document.body.removeChild(link)
					window.URL.revokeObjectURL(url)
				})
				track(EVENTS.DOCUMENT_DOWNLOADED, {
					document_type: "maker_invoice",
					download_result: "success",
					order_id: entityId(payload.orderId),
					order_reference: payload.orderRef,
					file_count: files.length,
					source: enumValue(payload.source),
				})
				return { downloaded: true, missing: false }
			} catch (error: any) {
				const status = error?.response?.status ?? error?.statusCode
				if (status === 404) {
					track(EVENTS.DOCUMENT_DOWNLOADED, {
						document_type: "maker_invoice",
						download_result: "missing",
						order_id: entityId(payload.orderId),
						order_reference: payload.orderRef,
						source: enumValue(payload.source),
					})
					return { downloaded: false, missing: true }
				}
				Sentry.captureException(error)
				dispatch("handleError", { error, description: "download maker invoice" }, { root: true })
				throw error
			}
		},
		async downloadMakerInvoice({ dispatch }, payload) {
			try {
				const config = {
					headers: {
						accept: "application/json",
						"x-access-token-user": useCookie("auth_token").value ?? "",
					},
					method: "GET",
					responseType: "json",
				}

				const response = await $fetch(
					`${import.meta.env.VITE_AND_DINE_API}/api/users/makers/downloadMakerInvoice/${payload.orderId}`,
					config
				)

				const { files } = response

				if (!files || !Array.isArray(files) || files.length === 0) {
					throw new Error("No invoice files received from server")
				}

				files.forEach((file: { fileName: string; data: string; contentType: string }) => {
					if (!file.fileName || !file.data) return

					const byteCharacters = atob(file.data)
					const byteNumbers = new Array(byteCharacters.length)
					for (let i = 0; i < byteCharacters.length; i++) {
						byteNumbers[i] = byteCharacters.charCodeAt(i)
					}
					const byteArray = new Uint8Array(byteNumbers)

					const blob = new Blob([byteArray], { type: file.contentType || "application/pdf" })

					const url = window.URL.createObjectURL(blob)
					const link = document.createElement("a")
					link.href = url
					link.setAttribute("download", file.fileName)
					document.body.appendChild(link)
					link.click()
					document.body.removeChild(link)
					window.URL.revokeObjectURL(url)
				})

				track(EVENTS.DOCUMENT_DOWNLOADED, {
					document_type: "maker_invoice",
					download_result: "success",
					order_id: entityId(payload.orderId),
					order_reference: payload.orderRef,
					file_count: files.length,
					source: enumValue(payload.source),
				})
			} catch (error) {
				Sentry.captureException(error)
				dispatch("handleError", { error, description: "download maker invoice" }, { root: true })
			}
		},
		async generateMakerInvoice({ dispatch, commit }, payload) {
			if (!payload || !payload.fromDate || !payload.toDate) {
				const error = new Error("From date and to date are required for generating invoices")
				Sentry.captureException(error)
				dispatch("handleError", { error, description: "generate maker invoices" }, { root: true })
				return
			}

			try {
				const body: Record<string, unknown> = {
					fromDate: payload.fromDate,
					toDate: payload.toDate,
				}
				if (payload.maker_id) body.maker_id = payload.maker_id

				const config = {
					method: "POST",
					headers: {
						"x-access-token-user": useCookie("auth_token").value ?? "",
						"Content-Type": "application/json",
					},
					body: JSON.stringify(body),
				}

				const response = await $fetch(
					`${import.meta.env.VITE_AND_DINE_API}/api/users/makers/generateMakerInvoices`,
					config
				)

				const from = new Date(payload.fromDate)
				const to = new Date(payload.toDate)
				track(EVENTS.MAKER_INVOICES_GENERATED, {
					// Batch vs per-maker tells us whether the batch flow is trusted.
					scope: payload.maker_id ? "single_maker" : "all_makers",
					maker_id: entityId(payload.maker_id),
					from_date: dateOnly(payload.fromDate),
					to_date: dateOnly(payload.toDate),
					period_days:
						!Number.isNaN(from.getTime()) && !Number.isNaN(to.getTime())
							? Math.round((to.getTime() - from.getTime()) / 86400000)
							: undefined,
				})

				commit("SET_SUCCESS_MESSAGE", response.message || "Invoices generated.", { root: true })
				setTimeout(() => {
					commit("SET_SUCCESS_MESSAGE", false, { root: true })
				}, 4000)
			} catch (error) {
				Sentry.captureException(error)
				dispatch("handleError", { error, description: "generate maker invoices" }, { root: true })
			}
		},
		async updateOrderProducts({ state, commit, dispatch }, payload) {
			const config = {
				headers: {
					accept: "application/json",
					"x-access-token-user": useCookie("auth_token").value ?? "",
					"content-type": "application/json",
				},
			}
			const { orderId, items } = payload

			// Captured before the mutation: the refetch below overwrites chosenOrder,
			// so reading "before" afterwards would report the new figures twice.
			const before = state?.chosenOrder
			const lineCountBefore = Array.isArray(before?.orderProducts)
				? before.orderProducts.length
				: undefined

			commit("SET_UPDATE_PRODUCTS_LOADING", true)
			try {
				await $fetch(
					`${import.meta.env.VITE_AND_DINE_API}/api/users/orders/updateProducts/${orderId}`,
					{ method: "PUT", body: { items }, ...config }
				)

				commit("SET_UPDATE_PRODUCTS_ERROR", false)

				await $fetch(
					`${import.meta.env.VITE_AND_DINE_API}/api/users/orders/${orderId}`,
					config
				).then((response) => {
					commit("SET_UPDATE_PRODUCTS_LOADING", false)
					commit("SET_CHOSEN_ORDER", response)

					const totalBefore = moneyGbp(before?.total)
					const totalAfter = moneyGbp(response?.total)
					track(EVENTS.ORDER_CONTENTS_UPDATED, {
						...orderProps(response),
						update_scope: "products",
						line_count_before: lineCountBefore,
						line_count_after: Array.isArray(response?.orderProducts)
							? response.orderProducts.length
							: undefined,
						total_gbp_before: totalBefore,
						total_gbp_after: totalAfter,
						// Rounded: subtracting two 2dp floats leaves a binary tail
						// (123.45 - 100.10 = 23.349999999999998).
						total_delta_gbp: gbpDelta(totalBefore, totalAfter),
					})
				})
			} catch (error) {
				commit("SET_UPDATE_PRODUCTS_LOADING", false)
				commit(
					"SET_UPDATE_PRODUCTS_ERROR",
					describeUpdateProductsError(error) ||
						"There was an error updating products for this order. If this error persists please reach out to support."
				)

				Sentry.captureException(error)
				dispatch("handleError", { error }, { root: true })
			}
		},
		async updateOrderFees({ state, commit, dispatch }, payload) {
			const config = {
				headers: {
					accept: "application/json",
					"x-access-token-user": useCookie("auth_token").value ?? "",
					"content-type": "application/json",
				},
			}

			const { orderId, orderFees } = payload
			const totalBefore = moneyGbp(state?.chosenOrder?.total)

			commit("SET_UPDATE_FEES_LOADING", true)
			try {
				await $fetch(
					`${import.meta.env.VITE_AND_DINE_API}/api/users/orders/updateFees/${orderId}`,
					{
						method: "PUT",
						body: { orderFees },
						...config,
					}
				)

				commit("SET_UPDATE_FEES_ERROR", false)

				await $fetch(
					`${import.meta.env.VITE_AND_DINE_API}/api/users/orders/${orderId}`,
					config
				).then((response) => {
					commit("SET_UPDATE_FEES_LOADING", false)
					commit("SET_CHOSEN_ORDER", response)

					const totalAfter = moneyGbp(response?.total)
					track(EVENTS.ORDER_CONTENTS_UPDATED, {
						...orderProps(response),
						update_scope: "fees",
						fee_count: Array.isArray(orderFees) ? orderFees.length : undefined,
						total_gbp_before: totalBefore,
						total_gbp_after: totalAfter,
						// Rounded: subtracting two 2dp floats leaves a binary tail
						// (123.45 - 100.10 = 23.349999999999998).
						total_delta_gbp: gbpDelta(totalBefore, totalAfter),
					})
				})
			} catch (error) {
				commit("SET_UPDATE_FEES_LOADING", false)
				commit(
					"SET_UPDATE_FEES_ERROR",
					"There was an error updating fees for this order. If this error persists please reach out to support."
				)

				Sentry.captureException(error)
				dispatch("handleError", { error }, { root: true })
			}
		},
		// Bulk mark-paid loops the existing single-order setPaid endpoint with
		// a small concurrency cap. Returns per-order success/failure so the
		// caller can surface partial failures in the toast.
		async bulkMarkOrdersPaid({ dispatch }, payload) {
			const { orderIds = [], paymentDate, paymentType = null, concurrency = 3 } = payload || {}
			if (!Array.isArray(orderIds) || orderIds.length === 0) {
				return { succeeded: [], failed: [] }
			}
			const config = {
				headers: {
					accept: "application/json",
					"x-access-token-user": useCookie("auth_token").value ?? "",
					"content-type": "application/json",
				},
			}
			const queue = [...orderIds]
			const succeeded: number[] = []
			const failed: { id: number; error: any }[] = []

			const runWorker = async () => {
				while (queue.length > 0) {
					const id = queue.shift() as number
					try {
						await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/users/orders/setPaid/${id}`, {
							method: "PUT",
							body: { paymentDate, paymentType },
							...config,
						})
						succeeded.push(id)
					} catch (error) {
						failed.push({ id, error })
						Sentry.captureException(error)
					}
				}
			}

			const workerCount = Math.min(concurrency, orderIds.length)
			await Promise.all(Array.from({ length: workerCount }, runWorker))

			// One event for the whole batch, after Promise.all — not one per order.
			// failed_count is the reconciliation-risk signal finance cares about.
			track(EVENTS.ORDER_MARKED_PAID, {
				scope: "bulk",
				payment_type: enumValue(paymentType),
				payment_date: dateOnly(paymentDate),
				order_count: orderIds.length,
				succeeded_count: succeeded.length,
				failed_count: failed.length,
			})

			if (failed.length > 0) {
				dispatch(
					"handleError",
					{
						error: failed[0].error,
						description: `mark ${failed.length} order${failed.length === 1 ? "" : "s"} as paid`,
					},
					{ root: true }
				)
			}
			return { succeeded, failed }
		},
		async updateOrderPaymentStatus({ commit, dispatch }, payload) {
			const config = {
				headers: {
					accept: "application/json",
					"x-access-token-user": useCookie("auth_token").value ?? "",
					"content-type": "application/json",
				},
			}
			const { orderId, paymentDate, paymentType = null } = payload
			try {
				await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/users/orders/setPaid/${orderId}`, {
					method: "PUT",
					body: { paymentDate, paymentType },
					...config,
				})

				const response = await $fetch(
					`${import.meta.env.VITE_AND_DINE_API}/api/users/orders/${orderId}`,
					config
				)

				commit("SET_CHOSEN_ORDER", response)
				track(EVENTS.ORDER_MARKED_PAID, {
					...orderProps(response),
					scope: "single",
					payment_type: enumValue(paymentType),
					payment_date: dateOnly(paymentDate),
					order_count: 1,
					succeeded_count: 1,
					failed_count: 0,
				})
				commit("SET_SUCCESS_MESSAGE", "Successfully updated order", { root: true })
				setTimeout(() => {
					commit("SET_SUCCESS_MESSAGE", false, { root: true })
				}, 5000)
			} catch (error) {
				dispatch(
					"handleError",
					{
						error,
						description: "updated order payment status",
					},
					{ root: true }
				)
				Sentry.captureException(error)
			}
		},
		async updateOrderPackage({ state, commit, dispatch }, payload) {
			const config = {
				headers: {
					accept: "application/json",
					"x-access-token-user": useCookie("auth_token").value ?? "",
					"content-type": "application/json",
				},
			}

			const { orderId, items, packages } = payload
			const totalBefore = moneyGbp(state?.chosenOrder?.total)
			commit("SET_UPDATE_PRODUCTS_LOADING", true)

			try {
				await $fetch(
					`${import.meta.env.VITE_AND_DINE_API}/api/users/orders/updatePackage/${orderId}`,
					{
						method: "PUT",
						body: JSON.stringify({ items, packages }),
						...config,
					}
				)

				commit("SET_UPDATE_PRODUCTS_ERROR", false)

				await $fetch(
					`${import.meta.env.VITE_AND_DINE_API}/api/users/orders/${orderId}`,
					config
				).then((response) => {
					commit("SET_UPDATE_PRODUCTS_LOADING", false)
					commit("SET_CHOSEN_ORDER", response)

					const totalAfter = moneyGbp(response?.total)
					track(EVENTS.ORDER_CONTENTS_UPDATED, {
						...orderProps(response),
						update_scope: "package",
						package_count: Array.isArray(packages) ? packages.length : undefined,
						line_count_after: Array.isArray(items) ? items.length : undefined,
						total_gbp_before: totalBefore,
						total_gbp_after: totalAfter,
						// Rounded: subtracting two 2dp floats leaves a binary tail
						// (123.45 - 100.10 = 23.349999999999998).
						total_delta_gbp: gbpDelta(totalBefore, totalAfter),
					})
				})
			} catch (error) {
				commit("SET_UPDATE_PRODUCTS_LOADING", false)
				commit(
					"SET_UPDATE_PRODUCTS_ERROR",
					"There was an error updating products for this order. If this error persists please reach out to support."
				)

				dispatch("handleError", { error }, { root: true })
			}
		},
		getShareCart({ dispatch }, id) {
			return $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/public/shareCart/${id}`, {})
		},
	},
	getters: {
		statusOptions: () => {
			// `Cancelled` stays in the list so a cancelled order displays its status
			// correctly, but it's disabled from selection — cancellation goes through
			// the Cancel button → `cancelOrderAdmin` action only.
			return [
				"Draft",
				"Created",
				"Cancelled",
				"Accepted",
				"Assigned",
				"Preparing",
				"Collected",
				"Delivered",
			].map((status) => ({
				value: status,
				name: status,
				disabled: status === "Cancelled",
			}))
		},
		paidOptions: () => {
			return ["Unpaid", "Paid", "Refunded", "Rejected", "Awaiting", "Credited"].map((status) => ({
				value: status,
				name: status,
			}))
		},
		deliveryServicesOptions: () => {
			return [
				{ value: "", name: "Not set" },
				{ value: "Closer2", name: "Closer2" },
				{ value: "Swiftee", name: "Swiftee" },
				{ value: "PedalMe", name: "PedalMe" },
				{ value: "Prestige", name: "Prestige" },
				{ value: "Maker", name: "Maker" },
				{ value: "Zhero", name: "Zhero" },
				{ value: "Mango", name: "Mango" },
			]
		},
		// Whether the current order has a payment or refund still settling on the
		// backend. Sourced from the backend-derived `order_has_inflight_transaction`
		// flag (computed via `orderPaymentService.isOrderInflight` in `getOrder`).
		// Drives the disabled state on Cancel / Update Products / Update Fees /
		// Mark as Paid plus the passive banner on the order detail page.
		hasInflightTransaction: (state) =>
			!!(state.chosenOrder && state.chosenOrder.order_has_inflight_transaction),
		isPaymentLinkAwaiting: (state) =>
			!!(
				state.chosenOrder &&
				state.chosenOrder.order_has_inflight_transaction &&
				state.chosenOrder.paid === "Awaiting" &&
				state.chosenOrder.payment_link_id
			),
	},
}

export default productStoreModule
